import React, { useState } from "react";

// Inline SVG icons (Feather-style) so the tab bar renders identically regardless of which
// icon font is loaded. Stroke uses currentColor so active/inactive tinting just works.
const ICONS = {
  car: (
    <>
      <path d="M5 13l1.6-4.2A2 2 0 0 1 8.5 7.5h7a2 2 0 0 1 1.9 1.3L19 13" />
      <path d="M3 13.5A1.5 1.5 0 0 1 4.5 12h15a1.5 1.5 0 0 1 1.5 1.5V17a1 1 0 0 1-1 1h-1M5 18H4a1 1 0 0 1-1-1v-3.5" />
      <circle cx="7.5" cy="18" r="1.6" />
      <circle cx="16.5" cy="18" r="1.6" />
    </>
  ),
  chat: (
    <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z" />
  ),
  pin: (
    <>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
};

function TabIcon({ name, className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}

// Native-style bottom navigation shown only on mobile (sm:hidden). The top segmented nav
// remains for desktop. `onNavigate` is the app's setView; `onLogout` opens the logout dialog.
export default function MobileTabBar({ view, onNavigate, isShopUser = false, isAdmin = false, onLogout }) {
  const [moreOpen, setMoreOpen] = useState(false);

  const tabs = [
    { key: "home", label: "Car Request", icon: "car" },
    { key: "messages", label: "Messages", icon: "chat" },
    { key: "shopExplorer", label: "Shops", icon: "pin" },
    { key: "profile", label: "Profile", icon: "user" },
  ];

  const moreItems = [
    ...(isShopUser ? [{ key: "shopPreview", label: "Shop Preview" }] : []),
    { key: "gettingStarted", label: "Getting Started" },
    ...(isAdmin ? [{ key: "admin", label: "Admin" }] : []),
  ];
  const moreActive = moreItems.some((item) => item.key === view);

  const go = (key) => {
    setMoreOpen(false);
    onNavigate(key);
  };

  const tabClass = (active) =>
    "flex flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium " +
    (active ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400");

  return (
    <>
      {moreOpen ? (
        <div className="sm:hidden fixed inset-0 z-40" onClick={() => setMoreOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            className="absolute inset-x-3 bottom-0 mb-[calc(4rem+env(safe-area-inset-bottom))] overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {moreItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => go(item.key)}
                aria-current={view === item.key ? "page" : undefined}
                className="block w-full border-b border-gray-100 dark:border-gray-700 px-4 py-3 text-left text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setMoreOpen(false);
                onLogout();
              }}
              className="block w-full px-4 py-3 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              Log Out
            </button>
          </div>
        </div>
      ) : null}

      <nav
        aria-label="Primary"
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 backdrop-blur pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-5">
          {tabs.map((tab) => {
            const active = view === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => go(tab.key)}
                aria-current={active ? "page" : undefined}
                className={tabClass(active)}
              >
                <TabIcon name={tab.icon} className="h-5 w-5" />
                <span className="leading-none">{tab.label}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setMoreOpen((open) => !open)}
            aria-expanded={moreOpen}
            className={tabClass(moreActive || moreOpen)}
          >
            <TabIcon name="menu" className="h-5 w-5" />
            <span className="leading-none">More</span>
          </button>
        </div>
      </nav>
    </>
  );
}
